import React, { useState, useRef, ReactNode, useEffect, useLayoutEffect, useCallback } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import { AtlassianIcon } from '@atlaskit/logo';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { WACAvatar } from '@/components/global-nav/user-profile-new/src/components/avatar';
import { wacToken } from '@/libraries/design/foundations';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { LogoV3 as Logo } from '@/libraries/wpl/foundations/src/logo-v3';

import { NavUtilities } from '../nav-utilities';
import SearchBar from '../search-bar';

import { DropdownType } from './global-nav-mobile-menu';
import { CloseIcon } from './icons/close';
import { MenuIcon } from './icons/menu';

const mobileLogoCss = css({
	display: 'flex',
	alignItems: 'center',
	[media.above.sm]: {
		display: 'none',
	},
});

const tabletLogoCss = css({
	display: 'none',
	[media.above.sm]: {
		display: 'flex',
		alignItems: 'center',
	},
});

const productPageLogoCss = css({
	gap: token('space.300'),
});

const logoCss = css({
	height: '21px',
});

const backButtonCss = css({
	fontSize: '0.875rem',
	color: token('color.text.selected'),
	display: 'flex',
	alignItems: 'center',
});

const backButtonDarkThemeCss = css({
	color: wacToken('color.extended.blue300'),
});

const profileAndMenuButtonCss = css({
	display: 'flex',
	gap: token('space.200'),
	alignContent: 'center',
});

const mobileNavUserProfileCss = css({
	display: 'none',
	[media.above.sm]: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
	},
});

const mobileIconContainerCss = css({
	height: '36px',
	display: 'flex',
	alignItems: 'center',
	[media.above.sm]: {
		'&&': { display: 'none' },
	},
});

const mobileDropdownMenuCss = css({
	display: 'none',

	[media.above.sm]: {
		opacity: 1,
		inset: '0px 0px auto auto',
		insetBlockStart: `calc(100% + ${token('space.050')})`,
		paddingBlockEnd: token('space.800'),
		minWidth: '380px',
		width: 'max-content',
		position: 'absolute',
		transform: 'translateX(-92%)',
		boxSizing: 'border-box',
		overflow: 'auto',
		borderRadius: '20px',
		borderBlockStart: '10px solid transparent',
		// eslint-disable-next-line local/ensure-wac-token-usage
		boxShadow: '0 0 1px 0 #ccc, 0 5px 20px -5px rgba(0,0,0,0.1)',
		order: 100,
		backgroundColor: wacToken('color.neutral.light0'),
		insetInlineStart: 0,
		transition: 'width 200ms ease',
		zIndex: 10,
		animation: 'fadein 0.25s 1',
	},
});

const mobileDropdownMenuDarkThemeCss = css({
	[media.above.sm]: {
		backgroundColor: wacToken('color.neutral.dark0'),
	},
});

const tabletIconContainerCss = css({
	display: 'none',

	[media.above.sm]: {
		display: 'flex',
		alignItems: 'center',
		position: 'relative',
	},
});

const profileCss = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',

	[media.above.sm]: {
		'&&': { display: 'none' },
	},
});

type MobileNavTopProps = {
	showLogo?: boolean;
	productKey: ProductKeyTypes;
	isLoggedIn: boolean;
	userName: string;
	picture: string;
	handleClickAvatar: () => void;
	showMenu: boolean;
	handleToggleMenu: (showMenu: boolean) => void;
	menuPage: DropdownType | string;
	handleMenuPage: (menuPage: DropdownType | string) => void;
	handleFirstOpen: (firstOpen: boolean) => void;
	mobileLogo?: JSX.Element;
	mobileLogoAriaLabel?: string;
	mobileLogoHref?: string;
	mobileLogoDataEventContainer?: string;
	isOpenSearch: boolean;
	isShowSearchButton?: boolean;
	setIsOpenSearch: React.Dispatch<React.SetStateAction<boolean>>;
	tabletDropdownMenu: ReactNode;
	isProductPage?: boolean;
	isShowTryNowLink?: boolean;
	navigationTrynowCtaText?: string;
	navigationTrynowCtaUrl?: string;
	navigationTrynowCtaOnClick?: (e: React.MouseEvent) => void;
	navigationTrynowCtaButtonVariant?: 'primary' | 'secondary';
	isShowAppSwitcher?: boolean;
	isShowUserProfile?: boolean;
	isShowTabletDropdown?: boolean;
	navType?: 'globalNav' | 'productNav';
};

const DefaultAtlassianLogo = (
	<Logo brand="atlassian" type="logo" appearance="brand" height={20} width={156} />
);

export const MobileNavTop = ({
	showLogo = true,
	productKey,
	isLoggedIn,
	userName,
	picture,
	handleClickAvatar,
	showMenu,
	handleToggleMenu,
	menuPage,
	handleMenuPage,
	handleFirstOpen,
	mobileLogo = DefaultAtlassianLogo,
	mobileLogoAriaLabel = 'Atlassian logo',
	mobileLogoHref = '/',
	mobileLogoDataEventContainer = 'globalNavWacV2',
	isShowSearchButton = true,
	isOpenSearch,
	setIsOpenSearch,
	tabletDropdownMenu,
	isProductPage = false,
	isShowTryNowLink = false,
	navigationTrynowCtaText,
	navigationTrynowCtaUrl,
	navigationTrynowCtaOnClick,
	navigationTrynowCtaButtonVariant = 'primary',
	isShowAppSwitcher = true,
	isShowUserProfile = true,
	isShowTabletDropdown = true,
	navType = 'globalNav',
}: // eslint-disable-next-line complexity
MobileNavTopProps) => {
	const intl = useIntl();
	const searchAndProfileRef = useRef<HTMLDivElement>(null);
	const atlasUrl = useLocalizedHref('/');
	const [mobileDropdownMenu, setMobileDropdownMenu] = useState<HTMLElement | null>(null);
	const { isDarkTheme } = useTheme();

	useLayoutEffect(() => {
		if (!showMenu || !mobileDropdownMenu) return;
		const panel = mobileDropdownMenu.querySelector<HTMLElement>('.mobile-dropdown-menu');
		const boundary =
			mobileDropdownMenu.closest('[data-nav-dropdown-boundary]') ??
			mobileDropdownMenu.closest('nav');
		if (!panel || !boundary) return;

		const updatePosition = () => {
			const containingBlock = panel.offsetParent;
			if (!containingBlock) return;
			const panelTop = boundary.getBoundingClientRect().bottom + 4;
			const viewport = window.visualViewport;
			const viewportBottom = viewport
				? viewport.offsetTop + viewport.height
				: document.documentElement.clientHeight;
			panel.style.insetBlockStart = `${panelTop - containingBlock.getBoundingClientRect().top}px`;
			panel.style.height = `${Math.max(0, viewportBottom - panelTop)}px`;
		};
		updatePosition();
		const observer = new ResizeObserver(updatePosition);
		observer.observe(boundary);
		observer.observe(mobileDropdownMenu);
		window.addEventListener('resize', updatePosition);
		window.addEventListener('scroll', updatePosition, { passive: true });
		window.visualViewport?.addEventListener('resize', updatePosition);
		window.visualViewport?.addEventListener('scroll', updatePosition);
		return () => {
			observer.disconnect();
			window.removeEventListener('resize', updatePosition);
			window.removeEventListener('scroll', updatePosition);
			window.visualViewport?.removeEventListener('resize', updatePosition);
			window.visualViewport?.removeEventListener('scroll', updatePosition);
		};
	}, [showMenu, mobileDropdownMenu]);

	const handleClickOutside = useCallback(
		(event: MouseEvent) => {
			const path = event.composedPath && event.composedPath();
			if (mobileDropdownMenu) {
				const style = window.getComputedStyle(mobileDropdownMenu);
				const insideTabletMobileDropdownMenu = Array.prototype.includes.call(
					Array.prototype.map.call(path, (el: { id: string }) => {
						return el.id === 'nav-tablet-mobile-dropdown-menu-button';
					}),
					true,
				);

				if (!insideTabletMobileDropdownMenu && style.display !== 'none') {
					handleToggleMenu(false);
					handleMenuPage('default');
					handleFirstOpen(false);
				}
			}
		},
		[mobileDropdownMenu, handleToggleMenu, handleMenuPage, handleFirstOpen],
	);

	const handleKeydown = useCallback(
		(event: KeyboardEvent) => {
			if (event.code === 'Escape' && showMenu) {
				handleToggleMenu(false);
				handleMenuPage('default');
				handleFirstOpen(false);
			}
		},
		[handleToggleMenu, handleMenuPage, handleFirstOpen, showMenu],
	);

	useEffect(() => {
		const element = document.getElementById('nav-tablet-mobile-dropdown-menu-button');
		setMobileDropdownMenu(element);
	}, []);

	useEffect(() => {
		document.addEventListener('click', handleClickOutside);
		document.addEventListener('keydown', handleKeydown);

		return () => {
			document.removeEventListener('click', handleClickOutside);
			document.removeEventListener('keydown', handleKeydown);
		};
	}, [handleClickOutside, handleKeydown]);

	return (
		<>
			{showLogo && (
				<>
					<div css={mobileLogoCss} className="mobile-nav-company-logo">
						{menuPage === 'default' ||
						!showMenu ||
						menuPage === 'search' ||
						menuPage === 'resources' ||
						menuPage === 'product-everyone' ||
						menuPage === 'product-developer' ||
						menuPage === 'product-product-managers' ||
						menuPage === 'product-it-professionals' ||
						menuPage === 'product-business-teams' ||
						menuPage === 'product-leadership-teams' ||
						menuPage === 'solutions' ||
						menuPage === 'ai' ||
						menuPage === 'why-atlassian' ? (
							<a
								css={logoCss}
								href={mobileLogoHref}
								aria-label={mobileLogoAriaLabel}
								data-event="clicked"
								data-event-component="linkButton"
								data-event-container={mobileLogoDataEventContainer}
								data-label={mobileLogoAriaLabel}
								data-product-key={productKey}
							>
								{mobileLogo}
							</a>
						) : (
							<button
								style={{ all: 'unset', width: '100%' }}
								onClick={(e) => {
									e.preventDefault();
									handleMenuPage('default');
									handleFirstOpen(false);
								}}
							>
								<div css={[backButtonCss, isDarkTheme && backButtonDarkThemeCss]}>
									<ChevronLeftIcon label="" spacing="spacious" />
									<span>
										{intl.formatMessage({
											id: 'globalnavv2.menu-back',
											defaultMessage: 'Back',
										})}
									</span>
								</div>
							</button>
						)}
					</div>
					<div
						css={[tabletLogoCss, isProductPage ? productPageLogoCss : '']}
						className="tablet-nav-company-logo"
					>
						{isProductPage && (
							<div
								className="product-page-nav-atlas-icon"
								css={css({
									height: '68px',
									width: '68px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									backgroundColor: wacToken('color.primary.blue'),
								})}
							>
								<a
									className="navigation-bar-atlas-icon"
									css={css({
										height: '56px',
										width: '56px',
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
									})}
									href={atlasUrl}
									aria-label={intl.formatMessage({
										id: 'productPageNav.logo-label',
										defaultMessage: 'Atlassian logo',
									})}
									data-event="clicked"
									data-event-component="linkButton"
									data-event-container="productPageNav"
									data-product-key="home"
								>
									<AtlassianIcon size="large" appearance="inverse" />
								</a>
							</div>
						)}
						<a
							css={logoCss}
							href={mobileLogoHref}
							aria-label={mobileLogoAriaLabel}
							data-event="clicked"
							data-event-component="linkButton"
							data-event-container={mobileLogoDataEventContainer}
							data-label={mobileLogoAriaLabel}
							data-product-key={productKey}
						>
							{mobileLogo}
						</a>
					</div>
				</>
			)}
			{isOpenSearch ? (
				<SearchBar isOpenSearch={isOpenSearch} setIsOpenSearch={setIsOpenSearch} isMobile={true} />
			) : (
				<div css={profileAndMenuButtonCss}>
					<div className="mobile-nav-user-profile" css={mobileNavUserProfileCss}>
						<NavUtilities
							isShowTryNowLink={isShowTryNowLink}
							productKey={productKey}
							isShowSearchButton={isShowSearchButton}
							showAiBackground={false}
							navigationTrynowCtaText={navigationTrynowCtaText}
							navigationTrynowCtaUrl={navigationTrynowCtaUrl}
							navigationTrynowCtaOnClick={navigationTrynowCtaOnClick}
							navigationTrynowCtaButtonVariant={navigationTrynowCtaButtonVariant}
							setIsOpenSearch={setIsOpenSearch}
							searchAndProfileRef={searchAndProfileRef}
							isShowAppSwitcher={isShowAppSwitcher}
							isShowUserProfile={isShowUserProfile}
							navType={navType}
						/>
					</div>
					{isShowUserProfile && isLoggedIn && userName && (
						<div className="mobile-nav-profile-avatar" css={profileCss}>
							<button
								style={{ all: 'unset' }}
								aria-label={intl.formatMessage({
									id: 'globalnavv2.menu-icon.toggle',
									defaultMessage: 'Toggle Menu',
								})}
								onClick={(e) => {
									e.preventDefault();
									handleClickAvatar();
								}}
							>
								<WACAvatar name={userName} src={picture} size={'medium'} />
							</button>
						</div>
					)}
					{/* Mobile button */}
					<div css={mobileIconContainerCss}>
						<button
							style={{ all: 'unset' }}
							aria-label={intl.formatMessage({
								id: 'globalnavv2.menu-icon.toggle',
								defaultMessage: 'Toggle Menu',
							})}
							className="globalnavV2ToggleMenu"
							onClick={(e) => {
								e.preventDefault();
								handleToggleMenu(!showMenu);
								handleMenuPage('default');
								handleFirstOpen(!showMenu);
							}}
						>
							{showMenu ? (
								<CloseIcon
									label={intl.formatMessage({
										id: 'globalnavv2.menu-icon.close',
										defaultMessage: 'Close Menu',
									})}
									lightMode={isDarkTheme}
								/>
							) : (
								<MenuIcon
									label={intl.formatMessage({
										id: 'globalnavv2.menu-icon.open',
										defaultMessage: 'Open Menu',
									})}
									lightMode={isDarkTheme}
								/>
							)}
						</button>
					</div>
					{/* Tablet button with dropdown */}
					{isShowTabletDropdown && (
						<div css={tabletIconContainerCss} id="nav-tablet-mobile-dropdown-menu-button">
							<button
								className="mobile-dropdown-menu-button"
								style={{
									all: 'unset',
									width: '35px',
									height: '35px',
								}}
								aria-label={intl.formatMessage({
									id: 'globalnavv2.menu-icon.toggle',
									defaultMessage: 'Toggle Menu',
								})}
								onClick={(e) => {
									e.preventDefault();
									handleToggleMenu(!showMenu);
									handleMenuPage('default');
									handleFirstOpen(!showMenu);
								}}
							>
								{showMenu ? (
									<CloseIcon
										label={intl.formatMessage({
											id: 'globalnavv2.menu-icon.close',
											defaultMessage: 'Close Menu',
										})}
										lightMode={isDarkTheme}
									/>
								) : (
									<MenuIcon
										label={intl.formatMessage({
											id: 'globalnavv2.menu-icon.open',
											defaultMessage: 'Open Menu',
										})}
										lightMode={isDarkTheme}
									/>
								)}
							</button>
							<div
								className="mobile-dropdown-menu"
								css={[
									mobileDropdownMenuCss,
									navType === 'globalNav' &&
										css({ [media.above.sm]: { paddingBlockEnd: token('space.500') } }),
									showMenu &&
										css({
											[media.above.sm]: {
												display: 'block',
											},
										}),
									isDarkTheme && mobileDropdownMenuDarkThemeCss,
								]}
							>
								{tabletDropdownMenu}
							</div>
						</div>
					)}
					{showMenu && (
						<div
							className="overlay"
							css={[
								css({
									position: 'fixed',
									display: 'block',
									width: '100%',
									height: '100%',
									insetBlockStart: '0',
									insetInlineStart: '0',
									insetInlineEnd: '0',
									insetBlockEnd: '0',
									// eslint-disable-next-line local/ensure-wac-token-usage
									backgroundColor: 'rgba(255,255,255,0.7)',
									zIndex: '-1',
								}),
								isDarkTheme &&
									css({
										// wacToken color.primary.black in rgb, with 0.7 opacity
										// eslint-disable-next-line local/ensure-wac-token-usage
										backgroundColor: 'rgba(16, 18, 20, 0.7)',
									}),
							]}
						/>
					)}
				</div>
			)}
		</>
	);
};
